import React, { useMemo, useState, useEffect } from "react";
import {
	TextField,
	Button,
	Typography,
	IconButton,
	Box,
	InputAdornment,
	Paper,
	CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { superAdminLoginAction, clearSuperAdminLoginAction } from "../../store/actions";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const SuperAdminLogin = () => {
	const [showPassword, setShowPassword] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { successData, loading, error, errorInfo } = useSelector((state) => state.superAdminLoginReducer);

	const [loginForm, setLoginForm] = useState({ phoneNumber: "", password: "" });
	const [touched, setTouched] = useState({});

	const togglePassword = () => setShowPassword((p) => !p);

	const loginErrors = useMemo(() => {
		const e = {};
		if (!loginForm.phoneNumber || !/^[6-9]\d{9}$/.test(loginForm.phoneNumber)) e.phoneNumber = "Enter a valid 10-digit phone";
		if (!loginForm.password) e.password = "Password is required";
		return e;
	}, [loginForm]);

	const textFieldStyle = {
		"& .MuiOutlinedInput-root": {
			borderRadius: "2px",
			alignItems: "center",
			width: "320px",
			maxWidth: "550px",
		},
		"& .MuiInputAdornment-root": {
			"& .MuiIconButton-root": {
				color: "#000",
				alignItems: "center",
			},
		},
	};

	const handleLoginSubmit = (e) => {
		e.preventDefault();
		setTouched((p) => ({ ...p, phoneNumber: true, password: true }));
		if (Object.keys(loginErrors).length) return;

		const payload = {
			phoneNumber: loginForm.phoneNumber,
			password: loginForm.password,
		};

		dispatch(superAdminLoginAction(payload));
	};

	useEffect(() => {
		setSubmitting(loading);
	}, [loading]);

	useEffect(() => {
		if (successData) {
			const user = successData?.user || successData?.data?.user || successData?.data;
			const details = {
				id: user?.id || user?._id || null,
				name: user?.name || user?.fullName || null,
				mobileNumber: user?.phoneNumber || user?.mobile || user?.mobileNumber || null,
				token: user?.token || successData?.token || null,
			};

			localStorage.setItem('userDetails', JSON.stringify(details));

			toast.success("Logged in successfully");
			navigate("/superAdmin/dashboard", { replace: true });
			dispatch(clearSuperAdminLoginAction());
		}
	}, [successData, navigate, dispatch]);

	useEffect(() => {
		if (error) {
			console.log("errorInfo", errorInfo);
			toast.error(errorInfo || "Login failed. Please try again.");
			dispatch(clearSuperAdminLoginAction());
		}
	}, [error, errorInfo, dispatch]);

	return (
		<Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", ml: { xs: 1, sm: 0, md: 0 }, mr: { xs: 1, sm: 0, md: 0 } }}>
			<Paper
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					width: { xs: "90%", md: "520px", sm: "500px" },
					maxWidth: "520px",
					borderRadius: "15px",
					overflow: "hidden",
					boxShadow: 3,
					p: 4,
					mt: { xs: 5, sm: 0, md: 0 }
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
					<Box sx={{ mb: 2 }}>
						<Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "#31303b", mb: 1 }}>
							Farmer 360
						</Typography>
						<Typography sx={{ color: "gray" }}>
							Please login to access your account.
						</Typography>
					</Box>


					<form onSubmit={handleLoginSubmit} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
						<TextField
							autoFocus
							margin="dense"
							id="login_phoneNumber"
							name="login_phoneNumber"
							label="Phone Number"
							type="text"
							variant="outlined"
							size="small"
							value={loginForm.phoneNumber}
							onChange={(e) => setLoginForm((p) => ({ ...p, phoneNumber: e.target.value.replaceAll(/\D/g, "") }))}
							onBlur={() => setTouched((p) => ({ ...p, phoneNumber: true }))}
							slotProps={{ htmlInput: { maxLength: 10 } }}
							inputMode="numeric"
							error={touched.phoneNumber && Boolean(loginErrors.phoneNumber)}
							helperText={touched.phoneNumber && loginErrors.phoneNumber}
							sx={textFieldStyle}
						/>
						<TextField
							margin="dense"
							id="login_password"
							name="login_password"
							label="Password"
							type={showPassword ? "text" : "password"}
							variant="outlined"
							size="small"
							value={loginForm.password}
							onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))}
							onBlur={() => setTouched((p) => ({ ...p, password: true }))}
							error={touched.password && Boolean(loginErrors.password)}
							helperText={touched.password && loginErrors.password}
							sx={textFieldStyle}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton aria-label="toggle password visibility" onClick={togglePassword} edge="end">
												{showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
											</IconButton>
										</InputAdornment>
									),
								},
							}}
						/>
						<Button
							type="submit"
							disabled={submitting}
							sx={{
								backgroundColor: "#00622c",
								color: "#fff",
								borderRadius: "15px",
								p: "10px",
								width: "100%",
								maxWidth: "300px",
								mb: 2,
								mt: 4,
								"&:disabled": { opacity: 0.8 },
							}}
						>

							{submitting ? (
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", color: "#fff" }}>
									<CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
									Logging in…
								</Box>
							) : (
								"Login"
							)}
						</Button>
					</form>

				</Box>
			</Paper>
		</Box>
	);
};

export default SuperAdminLogin;
